// components/BlogContent.client.tsx
"use client";

import * as React from "react";
import Image from "next/image";
import Link from "next/link";
import { PortableText, type PortableTextComponents } from "@portabletext/react";
import { LazyMotion, domAnimation, m } from "framer-motion";
import { FaXTwitter } from "react-icons/fa6";
import { BiLogoLinkedinSquare, BiLogoFacebookCircle } from "react-icons/bi";
import { buildSanityImage } from "@/sanity/lib/image";
import type { SanityImageSource } from "@sanity/image-url/lib/types/types";

type Img = { src: string; blurDataURL: string; alt: string } | null;

export type BlogContentClientData = {
    body: unknown[];
    authorName: string;
    authorImg: Img;
    shareLinks: { fb: string; x: string; li: string };
};

type ConflictingHtmlEvents =
    | "onAnimationStart"
    | "onAnimationEnd"
    | "onAnimationIteration"
    | "onDrag"
    | "onDragStart"
    | "onDragEnd"
    | "onDragEnter"
    | "onDragLeave"
    | "onDragOver"
    | "onDrop";

type SafeSectionProps = Omit<React.ComponentPropsWithoutRef<"section">, ConflictingHtmlEvents>;

export type BlogContentClientProps = SafeSectionProps & {
    data: BlogContentClientData;
};

const sizesFull = "(max-width: 768px) 100vw, (max-width: 1280px) 92vw, 1200px";
const sizesAvatar = "48px";

const slideUp = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: "easeOut" } },
};

const curtainAnim = {
    hidden: { y: "0%" },
    visible: { y: "100%", transition: { duration: 1.0, ease: [0.65, 0, 0.35, 1], delay: 0.2 } },
};

const imageZoom = {
    hidden: { scale: 1.1 },
    visible: { scale: 1, transition: { duration: 1.2, ease: "easeOut" } },
};

function BlogImage({ value }: { value?: SanityImageSource & { alt?: string; caption?: string } }) {
    if (!value) return null;

    const built = buildSanityImage(value, { w: 1200, h: 800, dpr: 2, q: 70 });
    const alt = value.alt ?? "";

    return (
        <m.figure
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={slideUp}
            className="relative mt-8 overflow-hidden rounded-3xl aspect-video w-full bg-secondary"
        >
            <m.div variants={imageZoom} className="relative h-full w-full">
                <Image
                    src={built.src}
                    alt={alt}
                    fill
                    sizes={sizesFull}
                    loading="lazy"
                    decoding="async"
                    placeholder={built.blurDataURL ? "blur" : "empty"}
                    blurDataURL={built.blurDataURL ?? undefined}
                    className="object-cover"
                />
            </m.div>
            <m.div variants={curtainAnim} className="absolute inset-0 z-10 bg-primary-light" />
            {value.caption && (
                <figcaption className="mt-2 text-sm opacity-70">{value.caption}</figcaption>
            )}
        </m.figure>
    );
}

type BlogTableValue = {
    caption?: string;
    hasHeader?: boolean;
    rows?: Array<{ cells?: string[] | null } | null> | null;
};

function BlogTable({ value }: { value?: BlogTableValue }) {
    const rows = (value?.rows ?? [])
        .filter((row): row is { cells?: string[] | null } => Boolean(row))
        .map((row) => (Array.isArray(row.cells) ? row.cells.map((cell) => cell ?? "") : []))
        .filter((cells) => cells.length > 0);

    if (rows.length === 0) return null;

    const hasHeader = value?.hasHeader !== false;
    const headerRow = hasHeader ? rows[0] : null;
    const bodyRows = hasHeader ? rows.slice(1) : rows;

    return (
        <figure className="my-8 w-full">
            <div className="w-full overflow-x-auto rounded-2xl border border-secondary/15">
                <table className="w-full min-w-[28rem] border-collapse text-left text-sm md:text-base">
                    {headerRow ? (
                        <thead className="bg-secondary/5">
                            <tr>
                                {headerRow.map((cell, index) => (
                                    <th
                                        key={`h-${index}`}
                                        scope="col"
                                        className="border-b border-secondary/15 px-3 py-2.5 font-semibold text-secondary md:px-4"
                                    >
                                        {cell}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                    ) : null}
                    <tbody>
                        {bodyRows.map((row, rowIndex) => (
                            <tr key={`r-${rowIndex}`} className="odd:bg-white/40">
                                {row.map((cell, cellIndex) => (
                                    <td
                                        key={`c-${rowIndex}-${cellIndex}`}
                                        className="border-b border-secondary/10 px-3 py-2.5 align-top text-secondary/85 md:px-4"
                                    >
                                        {cell}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {value?.caption?.trim() ? (
                <figcaption className="mt-2 text-sm opacity-70">{value.caption.trim()}</figcaption>
            ) : null}
        </figure>
    );
}

const portableTextComponents: PortableTextComponents = {
    block: {
        normal: ({ children }) => (
            <p className="mb-4 text-base leading-relaxed opacity-80 md:text-[18px]">{children}</p>
        ),
        h2: ({ children }) => (
            <h2 className="mt-10 mb-3 text-3xl font-semibold md:text-4xl">{children}</h2>
        ),
        h3: ({ children }) => (
            <h3 className="mt-8 mb-2 text-2xl font-semibold md:text-3xl">{children}</h3>
        ),
        h4: ({ children }) => (
            <h4 className="mt-6 mb-2 text-xl font-semibold md:text-2xl">{children}</h4>
        ),
        blockquote: ({ children }) => (
            <blockquote className="mb-4 border-l-2 border-black/20 pl-4 italic opacity-80">
                {children}
            </blockquote>
        ),
    },
    list: {
        bullet: ({ children }) => (
            <ul className="mb-4 ml-6 list-disc space-y-2 text-base opacity-80 md:text-lg">{children}</ul>
        ),
        number: ({ children }) => (
            <ol className="mb-4 ml-6 list-decimal space-y-2 text-base opacity-80 md:text-lg">{children}</ol>
        ),
    },
    marks: {
        link: ({ children, value }) => {
            const href = value?.href ?? "#";
            const isInternal = value?.internal === true;

            if (isInternal && href.startsWith("/")) {
                return (
                    <Link href={href} className="underline hover:text-accent">
                        {children}
                    </Link>
                );
            }

            return (
                <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-accent"
                >
                    {children}
                </a>
            );
        },
    },
    types: {
        image: ({ value }) => <BlogImage value={value} />,
        table: ({ value }) => <BlogTable value={value as BlogTableValue} />,
    },
};

export default function BlogContentClient({ data, className, ...rest }: BlogContentClientProps) {
    const { body, authorName, authorImg, shareLinks } = data;

    return (
        <LazyMotion features={domAnimation} strict>
            <m.section
                className={["px-[5%] pb-16 md:pb-24 lg:pb-28", className].filter(Boolean).join(" ")}
                {...rest}
            >
                <div className="container">
                    <div className="mx-auto max-w-lg overflow-hidden">
                        {body?.length ? (
                            <m.div
                                initial="hidden"
                                animate="visible"
                                variants={slideUp}
                            >
                                <PortableText
                                    value={body as import("@portabletext/react").PortableTextBlock[]}
                                    components={portableTextComponents}
                                />
                            </m.div>
                        ) : (
                            <p className="opacity-70">Brak treści do wyświetlenia.</p>
                        )}

                        <div className="my-12 h-px bg-black/30 -mx-6" />

                        <m.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            className="flex items-start justify-between gap-6"
                        >
                            <div className="flex items-center gap-4">
                                {authorImg && (
                                    <div className="overflow-hidden rounded-full relative size-12">
                                        <Image
                                            src={authorImg.src}
                                            alt={authorName || ""}
                                            fill
                                            sizes={sizesAvatar}
                                            loading="lazy"
                                            placeholder={authorImg.blurDataURL ? "blur" : "empty"}
                                            blurDataURL={authorImg.blurDataURL || undefined}
                                            className="object-cover"
                                        />
                                    </div>
                                )}

                                <div className="text-sm font-semibold uppercase tracking-wide leading-tight">
                                    {authorName &&
                                        authorName
                                            .trim()
                                            .split(/\s+/)
                                            .map((part, i) => <p key={i}>{part}</p>)}
                                </div>
                            </div>

                            <div className="flex flex-col items-end gap-2 text-right overflow-hidden">
                                <p className="text-sm font-semibold uppercase tracking-wide">Podziel się!</p>
                                <div className="flex items-center gap-3">
                                    <a
                                        href={shareLinks.fb}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="transition hover:text-accent"
                                    >
                                        <BiLogoFacebookCircle className="size-6" />
                                    </a>
                                    <a
                                        href={shareLinks.x}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="transition hover:text-accent"
                                    >
                                        <FaXTwitter className="size-5" />
                                    </a>
                                    <a
                                        href={shareLinks.li}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="transition hover:text-accent"
                                    >
                                        <BiLogoLinkedinSquare className="size-6" />
                                    </a>
                                </div>
                            </div>
                        </m.div>
                    </div>

                    <div className="mt-12 h-px w-full bg-black/30" />
                </div>
            </m.section>
        </LazyMotion>
    );
}
